import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@material-ui/core";
import {applyDocumentAmendments} from "../../word/taskpane/routes/checklist/utils/apply_document_amendments";
import {DocumentAmendment} from "common/flowmaster/types/amendment";
import type {
  AmendmentErrorEntry,
  OnLogAmendmentErrors,
} from "common_components/issue_detail/panels/components/amendments/amendment_group";
import buildAmendmentErrorEntry from "common_components/issue_detail/panels/components/amendments/build_amendment_error_entry";
import {getIsTrackAllChangesOn} from "word/taskpane/routes/checklist/utils/get_check_tracking_mode";
import {enableTrackAllChanges} from "word/taskpane/routes/checklist/utils/enable_track_changes_all";
import AmendmentsReport, {
  AmendmentHistory,
} from "common_components/document_amendments_report";
import fixBadLlmAmendments from "word/taskpane/routes/checklist/utils/amendments/fix_bad_llm_amendments";
import getAmendmentAnchorText, {
  AmendmentClauseTarget,
} from "utils/amendments/get_amendment_anchor_text";
import hashAmendment from "utils/amendments/hash_amendment";
import useSuppressedRenumberAmendments from "utils/amendments/use_suppressed_renumber_amendments";
import usePrevious from "utils/hooks/use_previous";
import {
  TrackChangesDialog,
  TrackChangesErrorDialog,
} from "common_components/track_changes_dialogs";

const styles = {
  container: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "0.5em",
  },
  button: {
    width: "100%",
  },
} as const;

interface DocumentAmendmentsButtonProps {
  amendments?: {
    amendments: DocumentAmendment[];
  } | null;
  appliedAmendmentHashes?: string[];
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  onAmendmentsApplied?: (
    appliedAmendments: DocumentAmendment[],
  ) => void | Promise<void>;
  onBulkApplyStart?: (amendments: DocumentAmendment[]) => void;
  onBulkApplyEnd?: () => void;
  scrollToClause?: (target: AmendmentClauseTarget) => void;
}

const DocumentAmendmentsButton: React.FC<DocumentAmendmentsButtonProps> = ({
  amendments,
  appliedAmendmentHashes,
  onLogAmendmentErrors,
  onAmendmentsApplied,
  onBulkApplyStart,
  onBulkApplyEnd,
  scrollToClause,
}) => {
  const [applying, setApplying] = useState(false);
  const [showTrackChangesDialog, setShowTrackChangesDialog] = useState(false);
  const [
    showTrackChangesErrorDialog,
    setShowTrackChangesErrorDialog,
  ] = useState(false);
  const [amendmentHistory, setAmendmentHistory] = useState<
    Array<{
      amendment: DocumentAmendment;
      hasFailed: boolean;
      subHistory?: AmendmentHistory[];
    }>
  >([]);
  const [showAmendmentReport, setShowAmendmentReport] = useState<boolean>(
    false,
  );
  const appliedAmendmentsRef = useRef<DocumentAmendment[]>([]);
  const fixedToOriginalRef = useRef<Map<DocumentAmendment, DocumentAmendment>>(
    new Map(),
  );

  const failedAmendmentErrors = useRef<AmendmentErrorEntry[]>([]);
  const runTotalRef = useRef<number>(0);

  const appliedHashSet = useMemo(() => new Set(appliedAmendmentHashes ?? []), [
    appliedAmendmentHashes,
  ]);

  const allAmendments = useMemo(() => amendments?.amendments ?? [], [
    amendments,
  ]);
  const {suppressed, amendmentsReady} = useSuppressedRenumberAmendments(
    allAmendments,
  );

  const unappliedAmendments = useMemo(
    () =>
      allAmendments.filter(
        amendment =>
          !appliedHashSet.has(hashAmendment(amendment)) &&
          !suppressed.has(amendment),
      ),
    [allAmendments, appliedHashSet, suppressed],
  );

  const handleScrollToClause = useCallback(
    (amendment: DocumentAmendment) => {
      const anchorText = getAmendmentAnchorText(amendment);
      if (anchorText && scrollToClause) {
        scrollToClause({text: anchorText});
      }
    },
    [scrollToClause],
  );

  const resetReport = useCallback(() => {
    setAmendmentHistory([]);
    setShowAmendmentReport(false);
    appliedAmendmentsRef.current = [];
    failedAmendmentErrors.current = [];
  }, []);

  const prevAppliedSize = usePrevious(appliedHashSet.size);
  useEffect(() => {
    if (prevAppliedSize && appliedHashSet.size === 0) {
      resetReport();
    }
  }, [appliedHashSet, prevAppliedSize, resetReport]);

  const addAmendmentFailure = (
    amendment: DocumentAmendment,
    errorMessage?: string,
  ) => {
    if (showAmendmentReport === false) {
      setShowAmendmentReport(true);
    }
    setAmendmentHistory(prev => [
      ...(prev ?? []),
      {amendment, hasFailed: true},
    ]);
    failedAmendmentErrors.current.push(
      buildAmendmentErrorEntry(
        amendment,
        errorMessage || "Amendment failed to apply",
      ),
    );
  };

  const addPartialAmendmentFailure = (
    amendment: DocumentAmendment,
    failures: Array<{amendment: DocumentAmendment; reason: string}>,
  ) => {
    if (showAmendmentReport === false) {
      setShowAmendmentReport(true);
    }

    const item = {
      amendment,
      hasFailed: true,
      subHistory: failures.map(fail => ({...fail, hasFailed: true})),
    } as AmendmentHistory;

    setAmendmentHistory(prev => [...(prev ?? []), item]);
    const errorMessage = `Amendment partially failed: ${failures
      .map(f => f.reason)
      .join(", ")}`;
    failedAmendmentErrors.current.push(
      buildAmendmentErrorEntry(amendment, errorMessage),
    );
  };

  const addAmendmentSuccess = (amendment: DocumentAmendment) => {
    if (showAmendmentReport === false) {
      setShowAmendmentReport(true);
    }
    const original = fixedToOriginalRef.current.get(amendment) ?? amendment;
    appliedAmendmentsRef.current.push(original);
    setAmendmentHistory(prev => [
      ...(prev ?? []),
      {amendment, hasFailed: false},
    ]);
  };
  const handleApplyAmendments = async (checkTrackChanges = true) => {
    if (unappliedAmendments.length === 0) {
      return;
    }
    resetReport();
    runTotalRef.current = unappliedAmendments.length;
    setApplying(true);

    if (checkTrackChanges) {
      const isTrackAllChangesOn = await getIsTrackAllChangesOn();

      if (!isTrackAllChangesOn) {
        setShowTrackChangesDialog(true);
        return;
      }
    }

    if (onBulkApplyStart) {
      onBulkApplyStart(unappliedAmendments);
    }

    try {
      const originalAmendments = unappliedAmendments;
      const fixedAmendments = fixBadLlmAmendments(originalAmendments);
      fixedToOriginalRef.current = new Map();
      fixedAmendments.forEach((fixed, i) => {
        fixedToOriginalRef.current.set(fixed, originalAmendments[i]);
      });
      await applyDocumentAmendments(
        fixedAmendments,
        addAmendmentFailure,
        addPartialAmendmentFailure,
        addAmendmentSuccess,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to apply amendments:", error);
    } finally {
      setApplying(false);
      setShowAmendmentReport(true);
      if (failedAmendmentErrors.current.length > 0 && onLogAmendmentErrors) {
        onLogAmendmentErrors(failedAmendmentErrors.current);
      }
      if (onAmendmentsApplied && appliedAmendmentsRef.current.length > 0) {
        await onAmendmentsApplied(appliedAmendmentsRef.current);
      } else if (onBulkApplyEnd) {
        onBulkApplyEnd();
      }
    }
  };

  const handleEnableTrackChangesAndApply = async () => {
    setShowTrackChangesDialog(false);

    try {
      await enableTrackAllChanges();
    } catch {
      setApplying(false);
      setShowTrackChangesErrorDialog(true);
      return;
    }
    await handleApplyAmendments();
  };

  const handleApplyWithoutTracking = async () => {
    setShowTrackChangesDialog(false);
    setShowTrackChangesErrorDialog(false);
    await handleApplyAmendments(false);
  };

  const handleCancelApplyAmendments = () => {
    setApplying(false);
    setShowTrackChangesDialog(false);
  };

  const showApplyButton =
    !showAmendmentReport && amendmentsReady && unappliedAmendments.length > 0;

  if (!showApplyButton && !showAmendmentReport) {
    return null;
  }
  return (
    <div style={styles.container}>
      <TrackChangesDialog
        open={showTrackChangesDialog}
        onClose={handleCancelApplyAmendments}
        onApplyWithoutTracking={handleApplyWithoutTracking}
        onConfirm={handleEnableTrackChangesAndApply}
      />
      <TrackChangesErrorDialog
        open={showTrackChangesErrorDialog}
        onClose={() => setShowTrackChangesErrorDialog(false)}
        onApplyWithoutTracking={handleApplyWithoutTracking}
      />
      {showApplyButton && (
        <Button
          variant="contained"
          color="primary"
          style={styles.button}
          onClick={() => handleApplyAmendments(true)}
          disabled={applying}
        >
          {`Apply ${unappliedAmendments.length} amendments`}
        </Button>
      )}
      {showAmendmentReport && (
        <div>
          <AmendmentsReport
            amendmentsHistory={amendmentHistory}
            isApplying={applying}
            totalAmendments={runTotalRef.current}
            onScrollToClause={handleScrollToClause}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentAmendmentsButton;
